import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { stageAPI } from '../services/api';
import { departementCourant } from '../services/droits';
import StageFicheLiaisonPublique from './StageFicheLiaisonPublique';
import StageDepotPublic from './StageDepotPublic';
import StageEvaluationPublique from './StageEvaluationPublique';
import { portailFictif, ficheFictive, pageFictive } from './StageParametrageModeles';
import './StagePublic.css';
import './SuiviStage.css';

/**
 * SUIVI DE STAGE — L'APERÇU D'UN LIEN D'ESSAI (lot 3.S-b2, décision SM8)
 * =======================================================================
 * Route : /stage/apercu/:type/:configId (type = fiche | depot | grille), connexion requise.
 *
 * Le lien d'un « M'envoyer un essai » ne doit créer AUCUN jeton : il ouvre ici le modèle de
 * la campagne, rendu par les VRAIES pages publiques en mode aperçu, sur des données
 * fictives — exactement l'aperçu de Paramétrage → Modèles. Rien n'est enregistré.
 */
const TITRES = { fiche: 'Fiche de liaison', depot: 'Portail de dépôt des documents', grille: "Grille d'évaluation" };

function StageApercuEnvoi({ config, user }) {
    const { type, configId } = useParams();
    const navigate = useNavigate();
    const departementId = departementCourant(user, config);
    const [donnees, setDonnees] = useState(null);
    const [erreur, setErreur] = useState(null);
    const [vue, setVue] = useState('etudiant');

    useEffect(() => {
        if (!departementId) return;
        stageAPI.apercuEnvoi(type, configId, departementId)
            .then((r) => setDonnees(r.data))
            .catch((e) => setErreur(e.response?.data?.error || "L'aperçu n'a pas pu être lu"));
    }, [type, configId, departementId]);

    let page = null;
    if (donnees && type === 'fiche') {
        page = <StageFicheLiaisonPublique key={vue} apercu={{ vue, contenu: ficheFictive(donnees.modele, vue) }} />;
    } else if (donnees && type === 'depot') {
        page = <StageDepotPublic apercu={portailFictif(donnees.modele)} />;
    } else if (donnees && type === 'grille') {
        page = <StageEvaluationPublique page={pageFictive('eval', donnees.modele)} apercu variante="eval" />;
    }

    return (
        <div style={{ padding: 20 }}>
            <div className="stg-encart stg-encart-info" style={{ marginBottom: 12 }}>
                <i className="fa-solid fa-flask" />
                <span>
                    <b>Aperçu d'un message d'essai</b> — {TITRES[type] || 'Page'}
                    {donnees ? <> · {donnees.configuration.formation_nom} {donnees.configuration.semestre} · modèle « {donnees.modele.nom} »</> : null}.
                    {' '}Données fictives : <b>rien n'est enregistré</b>. Le vrai destinataire, lui, reçoit son lien personnel.
                </span>
            </div>
            <div className="apercu-barre">
                <button type="button" className="btn btn-outline btn-sm" onClick={() => navigate('/stage')}>
                    <i className="fa-solid fa-arrow-left" /> Retour au suivi des stages
                </button>
                {type === 'fiche' && (
                    <div style={{ display: 'flex', gap: 4 }} role="group" aria-label="Vue de l'aperçu">
                        {[['etudiant', 'Vue étudiant'], ['maitre', 'Vue maître de stage']].map(([v, l]) => (
                            <button key={v} type="button" className={'btn btn-sm ' + (vue === v ? 'btn-green' : 'btn-outline')}
                                    aria-pressed={vue === v} onClick={() => setVue(v)}>{l}</button>
                        ))}
                    </div>
                )}
            </div>
            {erreur && <div className="stg-encart stg-encart-erreur"><i className="fa-solid fa-circle-exclamation" /><span>{erreur}</span></div>}
            {!donnees && !erreur && <div className="stg-attente"><i className="fa-solid fa-circle-notch stg-tourne" /><div>Lecture de l'aperçu…</div></div>}
            {page && <div className="apercu-cadre">{page}</div>}
        </div>
    );
}

export default StageApercuEnvoi;
